import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MssDataGridPro from '../../../common/MssDataGridPro';
import Loader from '../../../common/Loader';
import MssDelete from '../../../common/MssDelete';
import MssAlert from '../../../common/MssAlert';
import MssApiError from '../../../common/MssApiError';
import { SearchBar } from './SearchBar';
import { getColumns } from './columns';
import { useResources } from '../../helpers/useResources';

/**
 * The desktop table for one kind of resource, with its own search, row menu and
 * delete confirmation.
 *
 * Props:
 *   id           (string) - ID prefix for generated element IDs.
 *   resourceType (object) - One entry from RESOURCE_TYPES.
 */
function ResourcesTable({ id, resourceType }) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const {
    resources,
    isLoading,
    loadError,
    reload,
    isDeleting,
    resourceToDelete,
    requestDelete,
    cancelDelete,
    confirmDelete,
  } = useResources(resourceType);

  const { label, lowercasePlural, route, hasEditor } = resourceType;

  const visibleResources = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return resources;
    return resources.filter((resource) => resource.title?.toLowerCase().includes(term));
  }, [resources, searchTerm]);

  return (
    <Box className="resourcesTable" id={`${id}__box_resourcesTable`}>
      <Loader id={`${id}__delete`} isOpen={isDeleting} />
      {resourceToDelete && (
        <MssDelete
          id={`${id}__confirmDelete`}
          onToggle={cancelDelete}
          onDelete={confirmDelete}
          title={`Delete ${label}`}
          resourceName={resourceToDelete.title}
          isLoading={isDeleting}
          maxWidth="sm"
        />
      )}

      {loadError ? (
        <MssAlert id={`${id}__loadError`} severity="error" type="statusAlert">
          <MssApiError id={`${id}__resources`} widgetName={lowercasePlural} reload={reload} />
        </MssAlert>
      ) : (
        <>
          <SearchBar id={id} label={lowercasePlural} value={searchTerm} onChange={setSearchTerm} />
          <Box className="resourcesTableGrid">
            <MssDataGridPro
              id={`${id}__dataGrid`}
              className="mss__dataGrid"
              rows={visibleResources}
              columns={getColumns({
                id,
                titleHeader: label,
                onEdit: hasEditor ? (row) => navigate(`${route}/${row.id}/edit`) : undefined,
                onDelete: requestDelete,
              })}
              onRowClick={({ row }) =>
                navigate(`${route}/${row.id}`, { state: { title: row.title } })
              }
              sx={{ cursor: 'pointer' }}
              isLoading={isLoading}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 20 } },
                sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
              }}
              slots={{
                noRowsOverlay: () => (
                  <Box className="noRowsMessage">
                    {searchTerm.trim()
                      ? `No ${lowercasePlural} match your search.`
                      : `No ${lowercasePlural} found.`}
                  </Box>
                ),
              }}
              slotProps={{
                loadingOverlay: {
                  variant: 'linear-progress',
                  noRowsVariant: 'linear-progress',
                },
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export default ResourcesTable;
